import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ClerkProvider, SignedIn, SignedOut, RedirectToSignIn, RedirectToSignUp } from '@clerk/clerk-react';

import './index.css';
import './sw-register.js';
import { ErrorBoundary } from './ErrorBoundary.jsx';
import { Shell } from './Shell.jsx';
import { ToastProvider } from './components/Toast.jsx';
import { Home } from './routes/Home.jsx';
import { Projects } from './routes/Projects.jsx';
import { AuditLog } from './routes/AuditLog.jsx';
import { HelpInbox, ProjectHelpInbox } from './routes/HelpInbox.jsx';
import { HelpArticles } from './routes/HelpArticles.jsx';
import { HelpFeedback, ProjectHelpFeedback } from './routes/HelpFeedback.jsx';
import { Members } from './routes/Members.jsx';
import { Join } from './routes/Join.jsx';
import { Sites } from './routes/Sites.jsx';
import { Webhooks } from './routes/Webhooks.jsx';
import { Integrations } from './routes/Integrations.jsx';
import { Install } from './routes/Install.jsx';
import { Analytics } from './routes/Analytics.jsx';
import { SdkVersion } from './routes/SdkVersion.jsx';
import { TenantSettings } from './routes/TenantSettings.jsx';
import { EmailDomains } from './routes/EmailDomains.jsx';
import { UserProgress } from './routes/UserProgress.jsx';
import { WebhookSubs } from './routes/WebhookSubs.jsx';
import { Billing } from './routes/Billing.jsx';
import { MyKeys } from './routes/MyKeys.jsx';
import { Profile } from './routes/Profile.jsx';
import { ProjectBookmarklet } from './routes/ProjectBookmarklet.jsx';
import { ProjectDetail, ProjectOverview } from './routes/ProjectDetail.jsx';
import { Tours, Content, Themes, Checklists } from './routes/ProjectLists.jsx';
import { TourDetail } from './routes/TourDetail.jsx';
import { ThemeDetail } from './routes/ThemeDetail.jsx';
import { ContentDetail } from './routes/ContentDetail.jsx';
import { ChecklistDetail } from './routes/ChecklistDetail.jsx';
import { ApiKeys } from './routes/ApiKeys.jsx';
import { ProjectAuth } from './routes/ProjectAuth.jsx';
import { ProjectHelp } from './routes/ProjectHelp.jsx';
import { ComingSoon } from './routes/ComingSoon.jsx';
import { OAuthAuthorize } from './routes/OAuthAuthorize.jsx';
import { ConnectedApps } from './routes/ConnectedApps.jsx';
import { RequireTenant } from './components/RequireTenant.jsx';
import { SpotlightDogfood } from './spotlight-bootstrap.jsx';

/**
 * Clerk publishable key, burned in at build time via Vite.  The
 * invalid-key fallback we used to ship (`pk_test_missing`) silently
 * rendered a blank page because ClerkProvider errored out after
 * mount.  Instead, if the key's missing we render a real setup
 * page with copy-pasteable instructions.
 *
 * `VITE_DEV_AUTH_BYPASS=true` short-circuits the whole flow — the
 * ClerkProvider becomes a passthrough (see ./auth-bridge.js) and we
 * skip SetupPage even when no key is set.  Useful for Playwright
 * smoke tests + first-five-minutes against a fresh seed.
 */
const CLERK_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
const DEV_AUTH_BYPASS =
  import.meta.env.VITE_DEV_AUTH_BYPASS === 'true' ||
  import.meta.env.VITE_DEV_AUTH_BYPASS === '1';

function SetupPage() {
  return (
    <div className="setup-container">
      <div className="card setup-card">
        <h1>Spotlight dashboard — setup required</h1>
        <p>The dashboard needs a Clerk publishable key before it can render.</p>
        <ol>
          <li>
            Sign up free at{' '}
            <a href="https://clerk.com/" target="_blank" rel="noreferrer">clerk.com</a>
            {' '}and create an application.
          </li>
          <li>Copy the <b>Publishable key</b> (starts with <code>pk_test_…</code> or <code>pk_live_…</code>).</li>
          <li>
            Set it in your repo's <code>.env</code>:
            <pre className="setup-pre"><code>VITE_CLERK_PUBLISHABLE_KEY=pk_test_…</code></pre>
          </li>
          <li>
            Rebuild the dashboard container:
            <pre className="setup-pre"><code>docker compose up -d --build dashboard</code></pre>
          </li>
        </ol>
        <p style={{ color: 'var(--text-3)', fontSize: 13 }}>
          Docs:{' '}
          <a href="https://clerk.com/docs/quickstarts/react" target="_blank" rel="noreferrer">
            Clerk React quickstart
          </a>
          {' · '}
          Spotlight getting-started guide under <code>docs/GETTING_STARTED.md</code>.
        </p>
      </div>
      <style>{`
        .setup-container {
          min-height: 100vh;
          display: grid;
          place-items: center;
          padding: 24px;
          background: var(--surface-1);
        }
        .setup-card {
          max-width: 560px;
          width: 100%;
          padding: 32px;
          box-shadow: var(--shadow-3);
        }
        .setup-pre {
          background: var(--text-1);
          color: var(--surface-muted);
          padding: 10px 12px;
          border-radius: var(--r-md);
          font-size: 12.5px;
          margin-top: 6px;
          overflow-x: auto;
        }
        .setup-pre code {
          background: transparent;
          color: inherit;
          padding: 0;
        }
      `}</style>
    </div>
  );
}

function Protected({ children }) {
  return (
    <>
      <SignedIn>{children}</SignedIn>
      <SignedOut><RedirectToSignIn /></SignedOut>
    </>
  );
}

// Invitees reach /join?code=... before they have a Spotlight account —
// the default `RedirectToSignIn` UX of "Sign in to Spotlight.Help" reads
// as if they need an existing account.  Send them to sign-up instead.
// Clerk preserves the original URL via `redirect_url`, so they land
// back on /join?code=... after sign-up and the accept flow continues.
function JoinProtected({ children }) {
  return (
    <>
      <SignedIn>{children}</SignedIn>
      <SignedOut><RedirectToSignUp /></SignedOut>
    </>
  );
}

// Router basename — must line up with Vite's `base`.  Two deploy
// targets:
//   - docker-compose: served at `/dashboard` behind Traefik → '/dashboard'
//   - Cloudflare Pages: served at the root of the custom domain → '/'
// Driven by the same VITE_BASE_PATH env var that vite.config.js reads.
const ROUTER_BASENAME =
  (import.meta.env.VITE_BASE_PATH || '/dashboard/').replace(/\/$/, '') || '/';

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <ErrorBoundary>
    {(CLERK_KEY || DEV_AUTH_BYPASS) ? (
      <ClerkProvider publishableKey={CLERK_KEY || 'pk_dev_bypass'}>
        {/* Card 049 — dogfood the Spotlight SDK on our own dashboard. */}
        {/* Renders null; mounts a Pinpoint help widget when a Clerk */}
        {/* user is signed in.  See dashboard/src/spotlight-bootstrap.jsx. */}
        <SpotlightDogfood />
        {/* Card 044 — global toast surface.  Placed inside ClerkProvider */}
        {/* so toasts can reference Clerk state (e.g. "your session expired") */}
        {/* but outside BrowserRouter so a navigation doesn't unmount the */}
        {/* queue mid-toast. */}
        <ToastProvider>
        <BrowserRouter basename={ROUTER_BASENAME}>
          <Routes>
            <Route path="/join" element={<JoinProtected><Join /></JoinProtected>} />
            {/* MCP OAuth consent — no Shell chrome since the user */}
            {/* arrived here from an external app's auth flow and */}
            {/* shouldn't see the full dashboard sidebar mid-flow. */}
            <Route path="/oauth/authorize" element={<Protected><OAuthAuthorize /></Protected>} />
            <Route path="/" element={<Protected><Shell /></Protected>}>
              {/* Home is tenant-aware but degrades gracefully when */}
              {/* no tenant is reachable (every fetchSafe call swallows */}
              {/* the error), so we don't gate it.  Same for /join and */}
              {/* /oauth/authorize, which live entirely outside the */}
              {/* tenant scope.  Every other tenant-scoped route below */}
              {/* is wrapped in <RequireTenant> so the user sees a */}
              {/* friendly empty-state instead of a "Tenant not found" */}
              {/* red banner when membership hasn't resolved yet. */}
              <Route index element={<Home />} />
              <Route path="projects" element={<RequireTenant><Projects /></RequireTenant>} />
              <Route path="projects/:slug" element={<RequireTenant><ProjectDetail /></RequireTenant>}>
                <Route index element={<ProjectOverview />} />
                <Route path="tours" element={<Tours />} />
                <Route path="tours/:tour_id" element={<TourDetail />} />
                <Route path="content" element={<Content />} />
                <Route path="content/:content_id" element={<ContentDetail />} />
                <Route path="themes" element={<Themes />} />
                <Route path="themes/:theme_id" element={<ThemeDetail />} />
                <Route path="checklists" element={<Checklists />} />
                <Route path="checklists/:checklist_id" element={<ChecklistDetail />} />
                <Route path="api-keys" element={<ApiKeys />} />
                <Route path="sites" element={<Sites />} />
                {/* Card 096 — canonical project URLs match sidebar
                    + breadcrumb labels.  /auth and /help kept as
                    redirects so existing pinned links still work. */}
                <Route path="authentication" element={<ProjectAuth />} />
                <Route path="auth" element={<Navigate to="authentication" replace />} />
                <Route path="help-widget" element={<ProjectHelp />} />
                <Route path="help" element={<Navigate to="help-widget" replace />} />
                <Route path="help/inbox" element={<ProjectHelpInbox />} />
                <Route path="help/feedback" element={<ProjectHelpFeedback />} />
                <Route path="webhooks" element={<Webhooks />} />
                <Route path="analytics" element={<Analytics />} />
                {/* Card 063 — bookmarklet is a project-scoped */}
                {/* concept (one pk_live_* key per project tagged */}
                {/* purpose=bookmarklet), so the project route now */}
                {/* renders a real ProjectBookmarklet page instead */}
                {/* of bouncing the user out to /my-keys. */}
                <Route path="bookmarklet" element={<ProjectBookmarklet />} />
                <Route path="install" element={<Install />} />
              </Route>
              <Route path="members" element={<RequireTenant><Members /></RequireTenant>} />
              {/* Card 096 — tenant Pinpoint inbox lives at /inbox so
                  the URL matches the sidebar "Inbox" label.  Old /help
                  links still resolve via a permanent redirect.  Same
                  shape for Knowledge base — sidebar/URL/breadcrumb now
                  all read "Knowledge base". */}
              <Route path="inbox" element={<RequireTenant><HelpInbox /></RequireTenant>} />
              <Route path="help" element={<Navigate to="/inbox" replace />} />
              <Route path="knowledge-base" element={<RequireTenant><HelpArticles /></RequireTenant>} />
              <Route path="help/articles" element={<Navigate to="/knowledge-base" replace />} />
              <Route path="help/feedback" element={<RequireTenant><HelpFeedback /></RequireTenant>} />
              <Route path="audit" element={<RequireTenant><AuditLog /></RequireTenant>} />
              <Route path="settings" element={<RequireTenant><TenantSettings /></RequireTenant>} />
              <Route path="email-domains" element={<RequireTenant><EmailDomains /></RequireTenant>} />
              <Route path="users/:user_id?" element={<RequireTenant><UserProgress /></RequireTenant>} />
              <Route path="webhooks" element={<RequireTenant><WebhookSubs /></RequireTenant>} />
              {/* Card 053 — `/keys` is the tenant admin view (ApiKeys */}
              {/* in tenant-wide mode); `/my-keys` is the caller's own- */}
              {/* only view with mint affordance + bookmarklet drag */}
              {/* panel.  Old /keys readers (sidebar entry, user menu, */}
              {/* etc.) get re-pointed in the same patch. */}
              <Route path="keys" element={<RequireTenant><ApiKeys /></RequireTenant>} />
              <Route path="my-keys" element={<RequireTenant><MyKeys /></RequireTenant>} />
              {/* Card 055 phase 1 — Slack integration page (Connect + status). */}
              <Route path="integrations" element={<RequireTenant><Integrations /></RequireTenant>} />
              {/* Card 081 — OAuth-minted MCP tokens get a dedicated
                  surface separate from the catch-all /keys page so
                  the operator can revoke a specific Claude / Codex /
                  Cursor connection without scanning every sk_/pk_
                  row. */}
              <Route path="integrations/connected" element={<RequireTenant><ConnectedApps /></RequireTenant>} />
              {/* Personal profile — caller-scoped, not tenant-scoped, so no RequireTenant. */}
              <Route path="profile" element={<Profile />} />
              <Route path="billing" element={<RequireTenant><Billing /></RequireTenant>} />
              {/* Card 053 — the standalone bookmarklet generator is */}
              {/* gone.  Redirect any pinned tenant-wide /bookmarklet */}
              {/* link to the new home (My API keys → Bookmarklet */}
              {/* section anchor). */}
              <Route
                path="bookmarklet"
                element={<Navigate to="/my-keys#bookmarklet" replace />}
              />
              {/* Tenant-wide install page — same dropdown-over-projects */}
              {/* pattern; project sidebar surfaces the scoped variant. */}
              <Route path="install" element={<RequireTenant><Install /></RequireTenant>} />
              <Route path="sdk-version" element={<RequireTenant><SdkVersion /></RequireTenant>} />
              {/* Not yet implemented — honest placeholders so the */}
              {/* SDK overlay's link-outs don't silently bounce home. */}
              <Route path="billing" element={<ComingSoon title="Billing" blurb="Plan, usage, invoices, and seat management." />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        </ToastProvider>
      </ClerkProvider>
    ) : (
      <SetupPage />
    )}
    </ErrorBoundary>
  </React.StrictMode>,
);

